import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, RefreshCw, X, Tag,
  ToggleLeft, ToggleRight, Percent, DollarSign, ShoppingCart,
  Package, Clock, CheckCircle2, AlertCircle, ChevronDown,
  Search, CalendarDays, Zap,
} from 'lucide-react';
import { api } from '../api/client';
import { ConfirmModal } from './ConfirmModal';
import { useToast } from './Toast';

// ─── Types ───────────────────────────────────────────────────────────────────

type PromoType = 'bogo' | 'nxm' | 'percentage' | 'fixed' | 'bundle';

interface Product {
  id: string;
  name: string;
  sku: string;
  price: number;
}

interface Promotion {
  id:            string;
  productId:     string;
  name:          string;
  type:          PromoType;
  buyQty:        number | null;
  payQty:        number | null;
  discountValue: number | null;
  label:         string;
  description:   string | null;
  startsAt:      number | null;
  endsAt:        number | null;
  isActive:      boolean;
  createdAt:     number;
  updatedAt:     number;
  product:       Product;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PROMO_TYPES: { value: PromoType; label: string; icon: React.ReactNode; desc: string }[] = [
  {
    value: 'bogo',
    label: '2x1',
    icon:  <ShoppingCart className="w-4 h-4" />,
    desc:  'Llevás 2, el más barato es gratis',
  },
  {
    value: 'nxm',
    label: 'NxM',
    icon:  <Package className="w-4 h-4" />,
    desc:  'Llevás N unidades, pagás M (ej: 3x2)',
  },
  {
    value: 'percentage',
    label: '% OFF',
    icon:  <Percent className="w-4 h-4" />,
    desc:  'Descuento porcentual sobre el precio',
  },
  {
    value: 'fixed',
    label: 'Monto fijo',
    icon:  <DollarSign className="w-4 h-4" />,
    desc:  'Descuento de monto fijo en pesos',
  },
  {
    value: 'bundle',
    label: 'Pack',
    icon:  <Zap className="w-4 h-4" />,
    desc:  'Precio especial al llevar una cantidad mínima',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(n: number) {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
}

function toUnix(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : Math.floor(d.getTime() / 1000);
}

function fromUnix(ts: number | null): string {
  if (!ts) return '';
  return new Date(ts * 1000).toISOString().slice(0, 16);
}

function promoStatus(p: Promotion): { label: string; color: string; dot: string } {
  if (!p.isActive) return { label: 'Inactiva', color: 'text-ink-muted', dot: 'bg-ink-faint' };
  const now = Math.floor(Date.now() / 1000);
  if (p.startsAt && p.startsAt > now) return { label: 'Programada', color: 'text-amber-600', dot: 'bg-amber-400' };
  if (p.endsAt   && p.endsAt   < now) return { label: 'Vencida',    color: 'text-rose-600',  dot: 'bg-rose-400'  };
  return { label: 'Activa', color: 'text-emerald-600', dot: 'bg-emerald-400' };
}

function promoSummary(p: Promotion): string {
  switch (p.type) {
    case 'bogo':       return `Llevá ${p.buyQty}, pagá ${p.payQty}`;
    case 'nxm':        return `Llevá ${p.buyQty}, pagá ${p.payQty}`;
    case 'percentage': return `${p.discountValue}% de descuento`;
    case 'fixed':      return `${formatPrice(p.discountValue ?? 0)} de descuento por unidad`;
    case 'bundle':     return `${formatPrice(p.discountValue ?? 0)} por unidad al llevar ${p.buyQty}+`;
  }
}

// ─── Empty state ──────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  productId:     '',
  name:          '',
  type:          'nxm' as PromoType,
  buyQty:        '3',
  payQty:        '2',
  discountValue: '',
  label:         '',
  description:   '',
  startsAt:      '',
  endsAt:        '',
  isActive:      true,
};

type FormState = typeof EMPTY_FORM;

// ─── Main component ───────────────────────────────────────────────────────────

export const PromotionManager: React.FC = () => {
  const toast = useToast();

  // Data
  const [promos,   setPromos]   = useState<Promotion[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  // Modal
  const [isOpen,   setIsOpen]   = useState(false);
  const [editing,  setEditing]  = useState<Promotion | null>(null);
  const [saving,   setSaving]   = useState(false);
  const [formErrs, setFormErrs] = useState<string[]>([]);

  // Form
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<Promotion | null>(null);
  const [deleting,     setDeleting]     = useState(false);

  // Filters
  const [search,      setSearch]      = useState('');
  const [filterType,  setFilterType]  = useState<PromoType | 'all'>('all');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [promosRes, productsRes] = await Promise.all([
        api.get<Promotion[]>('/api/v1/promotions'),
        api.get<{ items: Product[] }>('/api/v1/products?limit=200'),
      ]);
      setPromos(promosRes.data);
      setProducts(productsRes.data.items ?? (productsRes.data as unknown as Product[]));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar promociones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPromos(); }, [fetchPromos]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormErrs([]);
    setIsOpen(true);
  }

  function openEdit(p: Promotion) {
    setEditing(p);
    setForm({
      productId:     p.productId,
      name:          p.name,
      type:          p.type,
      buyQty:        p.buyQty?.toString()        ?? '',
      payQty:        p.payQty?.toString()        ?? '',
      discountValue: p.discountValue?.toString() ?? '',
      label:         p.label,
      description:   p.description ?? '',
      startsAt:      fromUnix(p.startsAt),
      endsAt:        fromUnix(p.endsAt),
      isActive:      p.isActive,
    });
    setFormErrs([]);
    setIsOpen(true);
  }

  function closeModal() {
    if (saving) return;
    setIsOpen(false);
    setEditing(null);
  }

  // ── Auto-fill label ───────────────────────────────────────────────────────

  useEffect(() => {
    if (editing) return; // sólo en creación
    const type = form.type;
    const bq   = form.buyQty;
    const pq   = form.payQty;
    const dv   = form.discountValue;

    let label = '';
    if (type === 'bogo' || type === 'nxm') label = bq && pq ? `${bq}x${pq}` : '';
    if (type === 'percentage') label = dv ? `${dv}% OFF` : '';
    if (type === 'fixed')      label = dv ? `-$${dv}` : '';
    if (type === 'bundle')     label = bq && dv ? `Pack ${bq}u` : '';

    if (label) setField('label', label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.type, form.buyQty, form.payQty, form.discountValue]);

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave() {
    const errs: string[] = [];
    if (!form.productId)  errs.push('Seleccioná un producto');
    if (!form.name.trim()) errs.push('El nombre es requerido');
    if (!form.label.trim()) errs.push('El label (badge) es requerido');

    const bq = parseInt(form.buyQty);
    const pq = parseInt(form.payQty);
    const dv = parseInt(form.discountValue);

    if (['bogo', 'nxm'].includes(form.type)) {
      if (!bq || bq < 2)  errs.push('Cantidad a llevar (N) debe ser ≥ 2');
      if (!pq || pq < 1)  errs.push('Cantidad a pagar (M) debe ser ≥ 1');
      if (pq >= bq)       errs.push('La cantidad a pagar debe ser menor a la cantidad a llevar');
    }
    if (form.type === 'percentage') {
      if (!dv || dv < 1 || dv > 99) errs.push('El descuento debe ser entre 1% y 99%');
    }
    if (form.type === 'fixed') {
      if (!dv || dv < 1) errs.push('El monto de descuento debe ser mayor a $0');
    }
    if (form.type === 'bundle') {
      if (!bq || bq < 2) errs.push('La cantidad mínima del pack debe ser ≥ 2');
      if (!dv || dv < 1) errs.push('El precio especial del pack debe ser mayor a $0');
    }

    setFormErrs(errs);
    if (errs.length) return;

    setSaving(true);
    try {
      const payload = {
        productId:     form.productId,
        name:          form.name.trim(),
        type:          form.type,
        buyQty:        bq || null,
        payQty:        pq || null,
        discountValue: dv || null,
        label:         form.label.trim(),
        description:   form.description.trim() || null,
        startsAt:      toUnix(form.startsAt),
        endsAt:        toUnix(form.endsAt),
        isActive:      form.isActive,
      };

      if (editing) {
        await api.put(`/api/v1/promotions/${editing.id}`, payload);
        toast.success({ title: 'Promoción actualizada', description: form.name });
      } else {
        await api.post('/api/v1/promotions', payload);
        toast.success({ title: 'Promoción creada', description: form.name });
      }

      setIsOpen(false);
      await fetchPromos();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Error al guardar';
      setFormErrs([msg]);
      toast.error({ title: 'Error al guardar', description: msg });
    } finally {
      setSaving(false);
    }
  }

  // ── Toggle ────────────────────────────────────────────────────────────────

  async function handleToggle(p: Promotion) {
    try {
      await api.patch(`/api/v1/promotions/${p.id}/toggle`, {});
      toast.info({
        title: p.isActive ? 'Promoción desactivada' : 'Promoción activada',
        description: p.name,
      });
      await fetchPromos();
    } catch (e: unknown) {
      toast.error({ title: 'Error', description: e instanceof Error ? e.message : 'No se pudo cambiar el estado' });
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v1/promotions/${deleteTarget.id}`);
      toast.warning({ title: 'Promoción eliminada', description: deleteTarget.name });
      setDeleteTarget(null);
      await fetchPromos();
    } catch (e: unknown) {
      toast.error({ title: 'Error al eliminar', description: e instanceof Error ? e.message : 'Error desconocido' });
    } finally {
      setDeleting(false);
    }
  }

  // ── Filtered list ─────────────────────────────────────────────────────────

  const filtered = promos.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      p.name.toLowerCase().includes(q) ||
      p.product?.name.toLowerCase().includes(q) ||
      p.label.toLowerCase().includes(q);
    const matchType   = filterType   === 'all' || p.type === filterType;
    const status      = promoStatus(p);
    const matchActive =
      filterActive === 'all'      ? true :
      filterActive === 'active'   ? status.label === 'Activa' || status.label === 'Programada' :
                                    status.label === 'Inactiva' || status.label === 'Vencida';
    return matchSearch && matchType && matchActive;
  });

  // ─── Form fields by type ─────────────────────────────────────────────────

  const productPrice = products.find(p => p.id === form.productId)?.price ?? 0;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-2xl text-ink">Promociones</h2>
          <p className="text-sm text-ink-muted mt-0.5">
            {promos.length} promo{promos.length !== 1 ? 's' : ''} en total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPromos}
            disabled={loading}
            className="p-2.5 rounded-xl border border-page-border hover:bg-page-surface transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 text-ink-muted ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-primary hover:bg-brand-hover text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-brand-glow"
          >
            <Plus className="w-4 h-4" />
            Nueva promo
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar promo o producto…"
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-page-border rounded-xl bg-white placeholder-ink-faint focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
          />
        </div>

        {/* Type filter */}
        <div className="relative">
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value as PromoType | 'all')}
            className="appearance-none pl-3 pr-8 py-2.5 text-sm border border-page-border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border text-ink cursor-pointer"
          >
            <option value="all">Todos los tipos</option>
            {PROMO_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-faint pointer-events-none" />
        </div>

        {/* Status filter */}
        <div className="relative">
          <select
            value={filterActive}
            onChange={e => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
            className="appearance-none pl-3 pr-8 py-2.5 text-sm border border-page-border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border text-ink cursor-pointer"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activas / Programadas</option>
            <option value="inactive">Inactivas / Vencidas</option>
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-faint pointer-events-none" />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2.5 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-[88px] bg-page-surface rounded-2xl animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-16 text-ink-muted">
          <Tag className="w-10 h-10 mx-auto mb-3 text-ink-faint" />
          <p className="font-semibold text-ink">Sin promociones</p>
          <p className="text-sm mt-1">
            {search || filterType !== 'all' || filterActive !== 'all'
              ? 'Ninguna coincide con los filtros'
              : 'Creá tu primera promo con el botón de arriba'}
          </p>
        </div>
      )}

      {/* Promo cards */}
      {!loading && !error && (
        <div className="space-y-3">
          {filtered.map(p => {
            const status = promoStatus(p);
            const typeConfig = PROMO_TYPES.find(t => t.value === p.type);
            return (
              <div
                key={p.id}
                className={`glass-card border rounded-2xl px-5 py-4 flex items-center gap-4 animate-hover transition-all ${
                  !p.isActive ? 'opacity-60' : ''
                }`}
              >
                {/* Type badge */}
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-brand-surface border border-brand-border flex items-center justify-center text-brand-primary">
                  {typeConfig?.icon ?? <Tag className="w-4 h-4" />}
                </div>

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-ink truncate">{p.name}</span>
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-brand-surface text-brand-primary border border-brand-border tracking-wide">
                      {p.label}
                    </span>
                    <span className={`flex items-center gap-1 text-[10px] font-semibold ${status.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full inline-block ${status.dot}`} />
                      {status.label}
                    </span>
                  </div>
                  <p className="text-xs text-ink-muted mt-0.5 truncate">
                    {p.product?.name}
                    <span className="mx-1.5 text-ink-faint">·</span>
                    {promoSummary(p)}
                  </p>
                  {(p.startsAt || p.endsAt) && (
                    <p className="text-[10px] text-ink-faint mt-0.5 flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" />
                      {p.startsAt ? new Date(p.startsAt * 1000).toLocaleDateString('es-AR') : '—'}
                      {' → '}
                      {p.endsAt ? new Date(p.endsAt * 1000).toLocaleDateString('es-AR') : 'Sin vencimiento'}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggle(p)}
                    title={p.isActive ? 'Desactivar' : 'Activar'}
                    className="p-2 rounded-xl hover:bg-page-surface transition-colors"
                  >
                    {p.isActive
                      ? <ToggleRight className="w-5 h-5 text-emerald-500" />
                      : <ToggleLeft  className="w-5 h-5 text-ink-faint" />}
                  </button>
                  {/* Edit */}
                  <button
                    onClick={() => openEdit(p)}
                    className="p-2 rounded-xl hover:bg-page-surface transition-colors"
                  >
                    <Pencil className="w-4 h-4 text-ink-muted" />
                  </button>
                  {/* Delete */}
                  <button
                    onClick={() => setDeleteTarget(p)}
                    className="p-2 rounded-xl hover:bg-rose-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-rose-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Create / Edit Modal ─────────────────────────────────────────────── */}
      {isOpen && (
        <div className="fixed inset-0 z-[9000] flex items-start justify-center p-4 pt-12 overflow-y-auto">
          <div className="fixed inset-0 bg-ink/40 backdrop-blur-md animate-fade-in" onClick={closeModal} />

          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-page-border animate-fade-scale z-10 overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-page-border">
              <h3 className="font-heading font-bold text-lg text-ink">
                {editing ? 'Editar promoción' : 'Nueva promoción'}
              </h3>
              <button
                onClick={closeModal}
                disabled={saving}
                className="p-1.5 rounded-lg text-ink-faint hover:text-ink hover:bg-page-surface transition-colors disabled:opacity-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

              {/* Errors */}
              {formErrs.length > 0 && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl space-y-1">
                  {formErrs.map((e, i) => (
                    <p key={i} className="text-xs text-rose-700 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{e}
                    </p>
                  ))}
                </div>
              )}

              {/* Producto */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Producto *</label>
                <div className="relative">
                  <select
                    value={form.productId}
                    onChange={e => setField('productId', e.target.value)}
                    disabled={!!editing}
                    className="w-full appearance-none pl-3 pr-8 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border bg-white disabled:bg-page-surface disabled:text-ink-muted cursor-pointer"
                  >
                    <option value="">— Seleccioná un producto —</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {formatPrice(p.price)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-faint pointer-events-none" />
                </div>
                {editing && <p className="text-[10px] text-ink-faint">El producto no puede modificarse una vez creada la promo.</p>}
              </div>

              {/* Nombre */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Nombre interno *</label>
                <input
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  placeholder="Ej: 3x2 en Zapatillas Urbanas"
                  className="w-full px-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                />
              </div>

              {/* Tipo */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Tipo de promoción *</label>
                <div className="grid grid-cols-5 gap-2">
                  {PROMO_TYPES.map(t => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setField('type', t.value)}
                      title={t.desc}
                      className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border text-xs font-semibold transition-all ${
                        form.type === t.value
                          ? 'border-brand-primary bg-brand-surface text-brand-primary shadow-sm'
                          : 'border-page-border text-ink-muted hover:border-brand-border hover:text-ink'
                      }`}
                    >
                      {t.icon}
                      {t.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-ink-faint">
                  {PROMO_TYPES.find(t => t.value === form.type)?.desc}
                </p>
              </div>

              {/* Campos dinámicos por tipo */}
              <div className="space-y-4">
                {/* NxM / BOGO */}
                {(form.type === 'nxm' || form.type === 'bogo') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                        Llevás (N) *
                      </label>
                      <input
                        type="number"
                        min="2"
                        value={form.buyQty}
                        onChange={e => setField('buyQty', e.target.value)}
                        className="w-full px-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                        Pagás (M) *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={form.payQty}
                        onChange={e => setField('payQty', e.target.value)}
                        className="w-full px-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                      />
                    </div>
                  </div>
                )}

                {/* Percentage */}
                {form.type === 'percentage' && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                      Porcentaje de descuento (%) *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={form.discountValue}
                        onChange={e => setField('discountValue', e.target.value)}
                        className="w-full pl-3 pr-10 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint" />
                    </div>
                    {productPrice > 0 && form.discountValue && (
                      <p className="text-[11px] text-emerald-600">
                        Precio final: {formatPrice(productPrice * (1 - parseInt(form.discountValue) / 100))}
                      </p>
                    )}
                  </div>
                )}

                {/* Fixed */}
                {form.type === 'fixed' && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                      Descuento fijo ($ ARS por unidad) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-muted">$</span>
                      <input
                        type="number"
                        min="1"
                        value={form.discountValue}
                        onChange={e => setField('discountValue', e.target.value)}
                        className="w-full pl-7 pr-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                      />
                    </div>
                    {productPrice > 0 && form.discountValue && (
                      <p className="text-[11px] text-emerald-600">
                        Precio final: {formatPrice(Math.max(0, productPrice - parseInt(form.discountValue)))}
                      </p>
                    )}
                  </div>
                )}

                {/* Bundle */}
                {form.type === 'bundle' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                        Cantidad mínima *
                      </label>
                      <input
                        type="number"
                        min="2"
                        value={form.buyQty}
                        onChange={e => setField('buyQty', e.target.value)}
                        className="w-full px-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                        Precio especial/u ($) *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-muted">$</span>
                        <input
                          type="number"
                          min="1"
                          value={form.discountValue}
                          onChange={e => setField('discountValue', e.target.value)}
                          className="w-full pl-7 pr-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Label badge */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                  Etiqueta visible en la tienda *
                </label>
                <div className="flex items-center gap-2">
                  <input
                    value={form.label}
                    onChange={e => setField('label', e.target.value)}
                    placeholder="Ej: 3x2, 20% OFF, 2x1…"
                    className="flex-1 px-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                  />
                  {form.label && (
                    <span className="px-3 py-1.5 text-xs font-bold rounded-full bg-brand-primary text-white shadow-sm whitespace-nowrap">
                      {form.label}
                    </span>
                  )}
                </div>
              </div>

              {/* Vigencia */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide flex items-center gap-1">
                    <CalendarDays className="w-3 h-3" /> Inicio
                  </label>
                  <input
                    type="datetime-local"
                    value={form.startsAt}
                    onChange={e => setField('startsAt', e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                  />
                  <p className="text-[10px] text-ink-faint">Vacío = activa ya</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Vencimiento
                  </label>
                  <input
                    type="datetime-local"
                    value={form.endsAt}
                    onChange={e => setField('endsAt', e.target.value)}
                    className="w-full px-3 py-2.5 text-sm border border-page-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                  />
                  <p className="text-[10px] text-ink-faint">Vacío = sin vencimiento</p>
                </div>
              </div>

              {/* Descripción */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                  Descripción interna
                </label>
                <textarea
                  value={form.description}
                  onChange={e => setField('description', e.target.value)}
                  rows={2}
                  placeholder="Notas internas para el equipo…"
                  className="w-full px-3 py-2.5 text-sm border border-page-border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-border"
                />
              </div>

              {/* Activa */}
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <div
                  onClick={() => setField('isActive', !form.isActive)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${form.isActive ? 'bg-brand-primary' : 'bg-page-border'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isActive ? 'translate-x-5' : ''}`} />
                </div>
                <span className="text-sm text-ink font-medium">Activar al guardar</span>
                {form.isActive
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  : <AlertCircle  className="w-4 h-4 text-ink-faint" />}
              </label>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-page-border bg-page-surface">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 text-xs font-semibold text-ink-muted bg-white border border-page-border rounded-xl hover:bg-page-surface transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-brand-primary hover:bg-brand-hover text-white text-xs font-bold rounded-xl transition-all shadow-sm hover:shadow-brand-glow disabled:opacity-60"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {editing ? 'Guardar cambios' : 'Crear promoción'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        type="danger"
        title="Eliminar promoción"
        message={`¿Eliminás "${deleteTarget?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Sí, eliminar"
      />
    </div>
  );
};
